/**
 * GRANSKARE FÖR LAGET — chefens tre skärmar (attest, fakturera, inhyrda).
 *
 * RADERNA KOMMER UR granskare/rader.ts, och det är hela skillnaden mot förut:
 * filen bar en EGEN, komplett avskrift av granskarlagret, och det gjorde
 * arbetsorder.ts, moment.ts, personal.ts, priser.ts, synk.ts och tidrapport.ts
 * också. Sju kopior av samma sak, redan på väg isär. Se rader.ts.
 *
 * KVAR HÄR STÅR DET SOM INTE ÄR EN RAD: INVOICE_BASIS är serverns EGEN räkning
 * och kommer inte ur SYNC_PULL — dess svar har inga synkkolumner, ingen version
 * och ingen seq, och dess `lines` är en union över fyra radslag som bara finns
 * i den vyn. Den granskaren hör alltså hemma här och ingen annanstans.
 *
 * RETURTYPEN ÄR BEVISET, precis som i granskare/konto.ts: varje granskare nedan
 * är `Granskare<KONTRAKTETS TYP>`, så ett fält som läggs till i shared/types.ts
 * blir ett kompileringsfel HÄR i stället för ett tomt fält i drift.
 *
 * BESLUT 05 SITTER I TYPEN: approved och pending är två SKILDA
 * InvoiceBasisTotals och det finns ingen gemensam total att granska.
 *
 * LUCKAN SOM RITAS UT I STÄLLET FÖR ATT DÖLJAS: shared/ ger InvoicedFilter och
 * InvoiceBasisLine['kind'] som TYPER men inte som DATA. Listorna skrivs därför
 * av nedan, var och en med en `Uttommande`-rad som gör ett tillagt värde i
 * kontraktet till ett kompileringsfel här.
 */

import type {
  InvoiceBasisLine,
  InvoiceBasisResponse,
  InvoiceBasisTotals,
  InvoiceBasisWorkOrder,
  InvoicedFilter,
} from '@delat/types';
import { REPORTING_ROLES } from '@delat/routes';
import {
  Granskningsfel,
  accountId,
  arbetsdag,
  customerId,
  enAv,
  flagga,
  heltal,
  hundradelar,
  invoiceRunId,
  lista,
  meter,
  millisekunder,
  minuter,
  nullbar,
  objectKey,
  objekt,
  oren,
  orePerEnhet,
  orePerMil,
  orePerTimme,
  taskId,
  text,
  tusendel,
  workOrderId,
} from '../granska';
import type { Granskare } from '../granska';
import {
  FORDONEN,
  MATERIALENHETERNA,
  PRISKALLORNA,
  expenseId,
  materialLineId,
  mileageId,
  timeEntryId,
} from './rader';

/* == Vidare till skärmarna: raderna, kuvertet och felöversättningen ======
   Namnen är de laget-skärmarna redan importerar. Implementationen är rader.ts:s
   enda. Lägg ingenting nytt i den här listan — det som gäller alla skärmar hör
   hemma i rader.ts.
   ======================================================================= */

export {
  FORDONEN,
  FRANVAROSLAGEN,
  KALLORNA,
  KLIENTVYER,
  KUNDSLAGEN,
  MATERIALENHETERNA,
  ORDERLAGEN,
  PRISKALLORNA,
  VECKOLAGEN,
  granskaArbetsorder,
  granskaArbetsorderMontor,
  granskaFakturakorning,
  granskaFelkropp,
  granskaFirmainstallningar as granskaInstallningar,
  granskaFranvaro,
  granskaFullaAndringar,
  granskaGastandringar,
  granskaKund,
  granskaKundMontor,
  granskaMaterialrad,
  granskaMaterialradMontor,
  granskaMedlem,
  granskaMedlemMontor,
  granskaMilrad,
  granskaMilradMontor,
  granskaMoment,
  granskaMontorsandringar,
  granskaOpresultat,
  granskaOrderfoto,
  granskaOrdergast as granskaGasttilldelning,
  granskaPrislista,
  granskaPrisrad,
  granskaSyncPull,
  granskaSyncPush,
  granskaTidrad,
  granskaTidradMontor,
  granskaUtlagg,
  granskaUtlaggMontor,
  granskaVeckolage,
  somKlartidfel,
} from './rader';

/* == Listorna som bara INVOICE_BASIS behöver ============================= */

type Uttommande<TUnion extends string, TLista extends readonly string[]> = [TUnion] extends [
  TLista[number],
]
  ? true
  : never;

export const FAKTURAFILTREN = [
  'unbilled',
  'billed',
  'all',
] as const satisfies readonly InvoicedFilter[];
export type UttommandeFakturafiltren = Uttommande<InvoicedFilter, typeof FAKTURAFILTREN>;

const FAKTURASLAGEN = [
  'work',
  'material',
  'expense',
  'mileage',
] as const satisfies readonly InvoiceBasisLine['kind'][];
export type UttommandeFakturaslagen = Uttommande<InvoiceBasisLine['kind'], typeof FAKTURASLAGEN>;

/**
 * 0 eller 1. Kontraktet använder `0 | 1` och inte boolean på varje SQLite-flagga
 * (rot_eligible på underlagsraden), så granskaren måste göra detsamma — annars
 * står ett `true` i en typ som säger 1.
 */
const nollEtt = (varde: unknown, falt: string): 0 | 1 => {
  const n = heltal(varde, falt);
  if (n !== 0 && n !== 1) throw new Granskningsfel(falt, 'förväntade 0 eller 1');
  return n;
};

/* == INVOICE_BASIS ====================================================== */

const granskaSummor = (varde: unknown, falt: string): InvoiceBasisTotals => {
  const o = objekt(varde, falt);
  return {
    work_ore: oren(o['work_ore'], `${falt}.work_ore`),
    material_ore: oren(o['material_ore'], `${falt}.material_ore`),
    expense_ore: oren(o['expense_ore'], `${falt}.expense_ore`),
    mileage_ore: oren(o['mileage_ore'], `${falt}.mileage_ore`),
    total_ore: oren(o['total_ore'], `${falt}.total_ore`),
  };
};

export const granskaUnderlagsrad: Granskare<InvoiceBasisLine> = (v) => {
  const o = objekt(v, 'line');
  const gemensamt = {
    week_approved: flagga(o['week_approved'], 'line.week_approved'),
    invoice_run_id: nullbar(o['invoice_run_id'], 'line.invoice_run_id', (x) =>
      invoiceRunId(x, 'invoice_run_id'),
    ),
  };
  const slag = enAv(o['kind'], 'line.kind', FAKTURASLAGEN);
  switch (slag) {
    case 'work':
      return {
        ...gemensamt,
        kind: 'work',
        id: timeEntryId(o['id'], 'line.id'),
        work_date: arbetsdag(o['work_date'], 'line.work_date'),
        account_id: accountId(o['account_id'], 'line.account_id'),
        account_name: text(o['account_name'], 'line.account_name'),
        task_id: taskId(o['task_id'], 'line.task_id'),
        task_name: text(o['task_name'], 'line.task_name'),
        rot_eligible: nollEtt(o['rot_eligible'], 'line.rot_eligible'),
        minutes: minuter(o['minutes'], 'line.minutes'),
        rate_ore_per_hour: orePerTimme(o['rate_ore_per_hour'], 'line.rate_ore_per_hour'),
        rate_role: enAv(o['rate_role'], 'line.rate_role', REPORTING_ROLES),
        rate_source: enAv(o['rate_source'], 'line.rate_source', PRISKALLORNA),
        rot_hundredths: hundradelar(o['rot_hundredths'], 'line.rot_hundredths'),
        amount_ore: oren(o['amount_ore'], 'line.amount_ore'),
      };
    case 'material':
      return {
        ...gemensamt,
        kind: 'material',
        id: materialLineId(o['id'], 'line.id'),
        work_date: arbetsdag(o['work_date'], 'line.work_date'),
        description: text(o['description'], 'line.description'),
        quantity_milli: tusendel(o['quantity_milli'], 'line.quantity_milli'),
        unit: enAv(o['unit'], 'line.unit', MATERIALENHETERNA),
        unit_price_ore: orePerEnhet(o['unit_price_ore'], 'line.unit_price_ore'),
        amount_ore: oren(o['amount_ore'], 'line.amount_ore'),
      };
    case 'expense':
      return {
        ...gemensamt,
        kind: 'expense',
        id: expenseId(o['id'], 'line.id'),
        work_date: arbetsdag(o['work_date'], 'line.work_date'),
        description: text(o['description'], 'line.description'),
        receipt_object_key: nullbar(o['receipt_object_key'], 'line.receipt_object_key', (x) =>
          objectKey(x, 'receipt_object_key'),
        ),
        amount_ore: oren(o['amount_ore'], 'line.amount_ore'),
      };
    case 'mileage':
      return {
        ...gemensamt,
        kind: 'mileage',
        id: mileageId(o['id'], 'line.id'),
        work_date: arbetsdag(o['work_date'], 'line.work_date'),
        account_id: accountId(o['account_id'], 'line.account_id'),
        meters: meter(o['meters'], 'line.meters'),
        vehicle: enAv(o['vehicle'], 'line.vehicle', FORDONEN),
        rate_ore_per_10km: orePerMil(o['rate_ore_per_10km'], 'line.rate_ore_per_10km'),
        amount_ore: oren(o['amount_ore'], 'line.amount_ore'),
      };
  }
};

export const granskaUnderlagsorder: Granskare<InvoiceBasisWorkOrder> = (v) => {
  const o = objekt(v, 'work_orders[]');
  return {
    work_order_id: workOrderId(o['work_order_id'], 'work_orders[].work_order_id'),
    description: text(o['description'], 'work_orders[].description'),
    customer_id: customerId(o['customer_id'], 'work_orders[].customer_id'),
    customer_name: text(o['customer_name'], 'work_orders[].customer_name'),
    approved: granskaSummor(o['approved'], 'work_orders[].approved'),
    pending: granskaSummor(o['pending'], 'work_orders[].pending'),
    lines: lista(o['lines'], 'work_orders[].lines', granskaUnderlagsrad),
  };
};

/**
 * BESLUT 05 SITTER I TYPEN: approved och pending är två SKILDA
 * InvoiceBasisTotals och det finns ingen gemensam total att granska. Skärmen får
 * därför aldrig ett tal som blandar dem, och kan inte räkna fram ett heller utan
 * att skriva additionen själv.
 */
export const granskaFakturaunderlag: Granskare<InvoiceBasisResponse> = (v) => {
  const o = objekt(v, 'invoice_basis');
  return {
    from_date: arbetsdag(o['from_date'], 'from_date'),
    to_date: arbetsdag(o['to_date'], 'to_date'),
    invoiced_filter: enAv(o['invoiced_filter'], 'invoiced_filter', FAKTURAFILTREN),
    vat_rate_hundredths: hundradelar(o['vat_rate_hundredths'], 'vat_rate_hundredths'),
    approved: granskaSummor(o['approved'], 'approved'),
    pending: granskaSummor(o['pending'], 'pending'),
    work_orders: lista(o['work_orders'], 'work_orders', granskaUnderlagsorder),
    lines_truncated: flagga(o['lines_truncated'], 'lines_truncated'),
    generated_at: millisekunder(o['generated_at'], 'generated_at'),
  };
};
